/**
 * Authentication Context
 * Manages user authentication state and Firebase Auth operations
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  signInWithCustomToken as firebaseSignInWithCustomToken,
  signOut as firebaseSignOut,
  onAuthStateChanged
} from 'firebase/auth';
import { auth } from '../firebase';

const AUTH_URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:3001'
  : 'https://auth.findplayers.fr';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [guestMode, setGuestMode] = useState(false);

  useEffect(() => {
    // Always listen for Firebase auth state changes
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);

      if (user) {
        // Real authenticated user — clear guest mode if active
        localStorage.removeItem('guest-mode');
        setGuestMode(false);
      } else {
        // No Firebase user — check if guest mode is set
        const isGuest = localStorage.getItem('guest-mode') === 'true';
        setGuestMode(isGuest);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Handle auth callback with custom token from auth.findplayers.fr
  const handleAuthCallback = async (token) => {
    try {
      const result = await firebaseSignInWithCustomToken(auth, token);
      console.log('✅ Custom token sign-in successful:', result.user.email);
      return { success: true, user: result.user };
    } catch (error) {
      console.error('❌ Custom token sign-in error:', error);
      return { success: false, error: error.message };
    }
  };

  // Redirect to centralized auth site
  const redirectToAuth = (mode = 'login') => {
    const currentUrl = window.location.origin + window.location.pathname;
    const authUrl = `${AUTH_URL}/${mode}?redirect=${encodeURIComponent(currentUrl)}`;
    window.location.href = authUrl;
  };

  // Sign out
  const signOut = async () => {
    try {
      await firebaseSignOut(auth);

      // Clear guest mode if active
      if (guestMode) {
        localStorage.removeItem('guest-mode');
        setGuestMode(false);
      }

      // Clear tournament references
      localStorage.removeItem('currentTournamentId');

      console.log('✅ Sign out successful');
      return { success: true };
    } catch (error) {
      console.error('❌ Sign out error:', error);
      return { success: false, error: error.message };
    }
  };

  // Continue as guest
  const continueAsGuest = () => {
    localStorage.setItem('guest-mode', 'true');
    setGuestMode(true);
    setLoading(false);
    console.log('👤 Continuing as guest');
  };

  // Switch from guest to login mode
  const switchToLoginMode = () => {
    localStorage.removeItem('guest-mode');
    setGuestMode(false);
    console.log('🔄 Switching from guest to login mode');
  };

  // Check if user is signed in (false in guest mode)
  const isSignedIn = () => {
    return !guestMode && currentUser !== null;
  };

  // Get current user (null in guest mode)
  const getCurrentUser = () => {
    return guestMode ? null : currentUser;
  };

  const value = {
    currentUser,
    loading,
    guestMode,
    handleAuthCallback,
    redirectToAuth,
    signOut,
    continueAsGuest,
    switchToLoginMode,
    isSignedIn,
    getCurrentUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default AuthContext;
